// V5.44 : Page Admin - Détails complets d'une mission
// V5.71 : Bordereau de statut intégré + Protocole Zéro Trace
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, User, MapPin, FileText, Zap, Copy, Check, 
  Download, Eye, Loader2, AlertCircle, Phone, Mail,
  Calendar, Building2, Shield, Clock, Clipboard, ExternalLink,
  RefreshCw, Bell, Save, Edit2, ClipboardList, MessageCircle, Send,
  ChevronRight
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contract-cleaner/artifacts/7lw9nr1y_logo%20l%27effaceur.jpeg";

// V5.71 : 5 statuts officiels pour le bordereau admin
const ADMIN_STATUSES = [
  { key: 'nouveau', label: 'Lancement', color: 'cyan', step: 1 },
  { key: 'en_cours_de_traitement', label: 'Traitement', color: 'blue', step: 2 },
  { key: 'resiliation_envoyee', label: 'Expédition', color: 'purple', step: 3 },
  { key: 'validation_resiliation', label: 'Résiliation', color: 'orange', step: 4 },
  { key: 'termine', label: 'Effacé', color: 'green', step: 5 }
];

// V5.71 : Composant Bordereau de Statut (pilotage de la mission)
const StatusBordereau = ({ currentStatus, onStatusChange, updating }) => {
  const [selected, setSelected] = useState(currentStatus || 'nouveau');
  
  useEffect(() => { setSelected(currentStatus || 'nouveau'); }, [currentStatus]);
  
  const currentStep = ADMIN_STATUSES.find(s => s.key === selected)?.step || 1;
  
  return (
    <div className="glass-card p-5 border-2 border-cyan-400/40 bg-cyan-400/3 mb-6" data-testid="admin-status-bordereau">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-orbitron text-base font-bold text-cyan-400">Bordereau de Mission</h2>
        <span className="text-xs text-gray-500 font-mono">// PILOTAGE_STATUT</span>
      </div>
      
      {/* Barre de progression 5 étapes */}
      <div className="flex items-center justify-between relative mb-5">
        <div className="absolute top-3.5 left-6 right-6 h-0.5 bg-gray-700" />
        <div
          className="absolute top-3.5 left-6 h-0.5 bg-cyan-400 transition-all duration-700"
          style={{ width: `${Math.max(0, ((currentStep - 1) / 4) * 100)}%` }}
        />
        {ADMIN_STATUSES.map((s) => {
          const isPast = s.step < currentStep;
          const isActive = s.step === currentStep;
          const isSelected = s.key === selected;
          return (
            <button
              key={s.key}
              onClick={() => setSelected(s.key)}
              className={`flex flex-col items-center relative z-10 group`}
              title={s.label}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all border-2 ${
                isPast ? 'bg-cyan-400 border-cyan-400 text-black' :
                isActive ? 'bg-cyan-400/20 border-cyan-400 text-cyan-400' :
                isSelected ? 'bg-cyan-400/30 border-cyan-400 text-cyan-400' :
                'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-500'
              }`}>
                {isPast ? <Check className="w-3 h-3" /> : s.step}
              </div>
              <span className={`text-[9px] mt-1 font-mono whitespace-nowrap ${
                isPast || isActive ? 'text-cyan-400' : 'text-gray-600'
              }`}>{s.label}</span>
            </button>
          );
        })}
      </div>
      
      {/* Sélecteur + bouton Appliquer */}
      <div className="flex items-center gap-3">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="flex-1 bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:border-cyan-400 focus:outline-none"
          data-testid="admin-status-select"
        >
          {ADMIN_STATUSES.map(s => (
            <option key={s.key} value={s.key}>{s.step}. {s.label}</option>
          ))}
        </select>
        <Button
          onClick={() => onStatusChange(selected)}
          disabled={updating || selected === currentStatus}
          className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold px-5"
          data-testid="apply-status-btn"
        >
          {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
        </Button>
      </div>
    </div>
  );
};

// V5.48 : Intervalle de rafraîchissement automatique (30 secondes)
const AUTO_REFRESH_INTERVAL = 30000;

// V5.51 : Fonction pour générer le texte structuré du dossier (pour copie rapide)
// V5.52 : Amélioration avec TOUTES les données - Format complet pour agent IA
const generateDossierText = (mission) => {
  if (!mission) return '';
  
  const { client, addresses, technical, contracts } = mission;
  const displayId = mission.display_id || mission.mission_id;
  const missionInfo = mission.mission;
  
  // V5.52 : Construire l'adresse complète
  const formatAddress = (addr) => {
    if (!addr || (!addr.address && !addr.postal_code && !addr.city)) return 'Non communiqué';
    const parts = [addr.address, `${addr.postal_code || ''} ${addr.city || ''}`.trim()].filter(p => p);
    return parts.join(', ') || 'Non communiqué';
  };
  
  // V5.52 : Construire la liste des contrats
  const formatContracts = () => {
    if (!contracts || contracts.length === 0) return '   Aucun contrat sélectionné';
    return contracts.map((c, i) => 
      `   ${i + 1}. ${c.category_label || c.category} - ${c.provider || 'Non spécifié'} : ${c.action}`
    ).join('\n');
  };
  
  const lines = [
    `═══════════════════════════════════════════════════════════════`,
    `📋 DOSSIER CLIENT - ${displayId}`,
    `═══════════════════════════════════════════════════════════════`,
    ``,
    `▶ IDENTITÉ DU MANDANT`,
    `   Prénom : ${client?.first_name || 'Non communiqué'}`,
    `   Nom : ${client?.last_name || 'Non communiqué'}`,
    `   Email : ${client?.email || 'Non communiqué'}`,
    `   Téléphone : ${client?.phone || 'Non communiqué'}`,
    ``,
    `▶ LOGISTIQUE`,
    `   📍 Adresse de départ : ${formatAddress(addresses?.current)}`,
    `   📍 Adresse d'arrivée : ${formatAddress(addresses?.new)}`,
    `   📅 Date de déménagement : ${addresses?.moving_date ? new Date(addresses.moving_date).toLocaleDateString('fr-FR') : 'Non communiquée'}`,
    ``,
    `▶ CONTRATS À TRAITER`,
    formatContracts(),
    ``,
    `▶ DONNÉES TECHNIQUES`,
    `   PDL (Électricité) : ${technical?.pdl || 'En attente de saisie'}`,
    `   PCE (Gaz) : ${technical?.pce || 'En attente de saisie'}`,
    `   Notes : ${technical?.notes || 'Aucune note'}`,
    ``,
    `═══════════════════════════════════════════════════════════════`,
    `📊 STATUT : ${missionInfo?.status_label || missionInfo?.status || 'Nouveau'}`,
    `📦 PACK : ${missionInfo?.pack_type === 'demenagement' ? 'Déménagement' : missionInfo?.pack_type || 'Non défini'}`,
    `═══════════════════════════════════════════════════════════════`
  ];
  
  return lines.join('\n');
};

// Composant bouton "Copier"
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copié !`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };
  
  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-1 rounded hover:bg-cyan-400/20 transition-colors"
      title={`Copier ${label}`}
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-gray-400 hover:text-cyan-400" />
      )}
    </button>
  );
};

// V5.58 : Composant pour afficher un champ avec bouton copier
// Affiche une alerte rouge si une donnée critique est absente
const DataField = ({ label, value, icon: Icon, copyable = true, critical = false }) => {
  const isEmpty = !value || value === '-' || value === 'Non communiqué';
  const showError = critical && isEmpty;
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${showError ? 'bg-red-900/30 border border-red-500/50' : 'bg-gray-800/50'}`}>
      <div className="flex items-center gap-3">
        {Icon && <Icon className={`w-4 h-4 ${showError ? 'text-red-400' : 'text-cyan-400'}`} />}
        <div>
          <p className={`text-xs ${showError ? 'text-red-400' : 'text-gray-400'}`}>{label}</p>
          {showError ? (
            <p className="text-red-400 font-mono text-sm flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              ⚠️ Erreur de synchronisation tunnel
            </p>
          ) : (
            <p className="text-white font-mono text-sm">{value || '-'}</p>
          )}
        </div>
      </div>
      {copyable && value && <CopyButton text={value} label={label} />}
    </div>
  );
};

// Catégories avec icônes
const CATEGORY_CONFIG = {
  electricite: { label: 'Électricité', icon: Zap, color: 'text-yellow-400' },
  gaz: { label: 'Gaz', icon: Zap, color: 'text-orange-400' },
  eau: { label: 'Eau', icon: Zap, color: 'text-blue-400' },
  internet: { label: 'Internet', icon: Building2, color: 'text-purple-400' },
  assurance: { label: 'Assurance', icon: Shield, color: 'text-green-400' },
  mutuelle: { label: 'Mutuelle', icon: Shield, color: 'text-pink-400' },
  banque: { label: 'Banque', icon: Building2, color: 'text-cyan-400' },
  sport: { label: 'Sport', icon: Building2, color: 'text-red-400' },
  presse: { label: 'Presse', icon: FileText, color: 'text-gray-400' }
};

export default function AdminMissionDetailsPage() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(null);
  const [error, setError] = useState(null);
  
  // V5.48 : États pour auto-refresh et notifications
  const [lastUpdate, setLastUpdate] = useState(null);
  const [hasNewData, setHasNewData] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const previousDataRef = useRef(null);
  
  // V5.48 : États pour édition inline des données techniques
  const [editingTechnical, setEditingTechnical] = useState(false);
  const [technicalData, setTechnicalData] = useState({ pdl: '', pce: '', notes: '' });
  const [savingTechnical, setSavingTechnical] = useState(false);
  
  // V5.63 : État pour la Consigne Expert (messagerie Admin → Client)
  const [consigneExpert, setConsigneExpert] = useState('');
  const [savingConsigne, setSavingConsigne] = useState(false);
  const [editingConsigne, setEditingConsigne] = useState(false);
  
  // V5.51 : États pour Copie Rapide et Téléchargement
  const [copyingDossier, setCopyingDossier] = useState(false);
  const [downloadingMandat, setDownloadingMandat] = useState(false);
  
  // V5.54 : Export CSV isolé (ce dossier uniquement)
  const [exportingCSV, setExportingCSV] = useState(false);
  
  // V5.71 : État pour le bordereau de statut inline
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // V5.54 : Export CSV de CE dossier uniquement (pas tous les dossiers)
  const handleExportSingleCSV = async () => {
    if (!mission?.mission_id) {
      toast.error('Mission ID non disponible');
      return;
    }
    
    setExportingCSV(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/missions/${mission.mission_id}/export-csv`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dossier_${mission.display_id || mission.mission_id}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('📊 Export CSV du dossier téléchargé !');
    } catch (err) {
      console.error('Erreur export CSV:', err);
      toast.error('Erreur lors de l\'export CSV');
    } finally {
      setExportingCSV(false);
    }
  };
  
  // V5.71 : Mettre à jour le statut depuis la page détails
  const handleStatusChange = async (newStatus) => {
    if (!mission?.mission_id) return;
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/missions/update-status`, {
        mission_id: mission.mission_id,
        new_status: newStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(`Statut mis à jour : ${newStatus}`);
      fetchMissionDetails(); // Rafraîchir les données
    } catch {
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  // V5.51 : Copie Rapide du dossier complet
  const handleCopyDossier = async () => {
    if (!mission) return;
    setCopyingDossier(true);
    try {
      const text = generateDossierText(mission);
      await navigator.clipboard.writeText(text);
      toast.success('📋 Dossier complet copié !', { duration: 3000 });
    } catch (err) {
      toast.error('Erreur lors de la copie');
    } finally {
      setCopyingDossier(false);
    }
  };
  
  // V5.51 : Téléchargement du mandat PDF
  const handleDownloadMandat = async () => {
    if (!mission?.mandat?.mandat_id) {
      toast.error('Aucun mandat signé trouvé pour ce dossier');
      return;
    }

    setDownloadingMandat(true);
    try {
      const token = localStorage.getItem('token');
      // V1.2 FIX : bon préfixe /mission-mandats (le router a prefix="/mission-mandats")
      const response = await axios.get(
        `${API}/mission-mandats/${mission.mandat.mandat_id}/download`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob',
          withCredentials: true
        }
      );

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandat_${mission.mission_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('📄 Mandat téléchargé !');
    } catch (err) {
      console.error('Erreur téléchargement mandat:', err);
      toast.error('Erreur lors du téléchargement du mandat');
    } finally {
      setDownloadingMandat(false);
    }
  };

  // DDD.9 : Export complet du dossier en ZIP — CSV + tous les mandats + docs client
  const handleDownloadBundle = async () => {
    if (!mission?.mission_id) {
      toast.error('Mission non identifiée');
      return;
    }
    setDownloadingMandat(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/admin/missions/${mission.mission_id}/export-bundle`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob',
          withCredentials: true
        }
      );
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dossier_${mission.mission_id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('📦 Dossier complet téléchargé (CSV + mandats + docs)');
    } catch (err) {
      console.error('Erreur export bundle:', err);
      toast.error('Erreur lors du téléchargement du dossier complet');
    } finally {
      setDownloadingMandat(false);
    }
  };

  // V1.2 : Téléchargement d'un mandat unitaire (1 organisme) pour l'agent
  const handleDownloadUnitMandat = async (categoryId, categoryLabel) => {
    if (!mission?.mandat?.mandat_id) {
      toast.error('Aucun mandat signé trouvé');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/mission-mandats/${mission.mandat.mandat_id}/unit/${categoryId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob',
          withCredentials: true
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandat_${categoryId}_${mission.mission_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`📄 Mandat ${categoryLabel} téléchargé !`);
    } catch (err) {
      console.error('Erreur téléchargement mandat unitaire:', err);
      toast.error(`Erreur : mandat ${categoryLabel} introuvable`);
    }
  };

  // V5.56 : Vérifier l'accès admin et charger les données
  // CORRECTION: Ne pas rediriger immédiatement si pas de token, essayer quand même l'API
  const fetchMissionDetails = useCallback(async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      
      // V5.56 : Essayer de charger même sans token (l'API vérifiera l'auth)
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/admin/missions/${missionId}`, {
        headers,
        withCredentials: true
      });
      
      // V5.48 : Détecter les changements pour notification
      if (previousDataRef.current && silent) {
        const prevData = JSON.stringify(previousDataRef.current);
        const newData = JSON.stringify(res.data);
        if (prevData !== newData) {
          setHasNewData(true);
          toast.success('🔔 Nouvelles données reçues pour ce dossier', {
            duration: 5000,
            icon: <Bell className="w-4 h-4" />
          });
        }
      }
      
      previousDataRef.current = res.data;
      setMission(res.data);
      setLastUpdate(new Date());
      setError(null);
      
      // V5.48 : Initialiser les données techniques pour édition
      setTechnicalData({
        pdl: res.data?.technical?.pdl || '',
        pce: res.data?.technical?.pce || '',
        notes: res.data?.technical?.notes || ''
      });
      
      // V5.63 : Initialiser la consigne expert
      setConsigneExpert(res.data?.consigne_expert?.text || '');
      
    } catch (err) {
      console.error('Erreur chargement mission:', err);
      if (err.response?.status === 401) {
        // V5.56 : Rediriger vers login SEULEMENT si non authentifié
        navigate('/login', { state: { returnTo: `/admin/missions/${missionId}` } });
      } else if (err.response?.status === 403) {
        setError('Accès non autorisé. Réservé aux administrateurs.');
      } else if (err.response?.status === 404) {
        setError('Mission non trouvée.');
      } else {
        setError('Erreur lors du chargement des données.');
      }
    } finally {
      setLoading(false);
    }
  }, [missionId, navigate]);
  
  // Chargement initial
  useEffect(() => {
    fetchMissionDetails();
  }, [fetchMissionDetails]);
  
  // V5.48 : Auto-refresh toutes les 30 secondes
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const intervalId = setInterval(() => {
      fetchMissionDetails(true); // Mode silencieux (pas de loading spinner)
    }, AUTO_REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [autoRefreshEnabled, fetchMissionDetails]);
  
  // V5.48 : Sauvegarder les données techniques modifiées par l'admin
  const handleSaveTechnical = async () => {
    setSavingTechnical(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/missions/${missionId}/technical`, technicalData, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success('Données techniques mises à jour !');
      setEditingTechnical(false);
      fetchMissionDetails(); // Rafraîchir les données
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingTechnical(false);
    }
  };
  
  // V5.63 : Sauvegarder la Consigne Expert
  const handleSaveConsigne = async () => {
    setSavingConsigne(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/missions/${missionId}/consigne`, {
        consigne_expert: consigneExpert
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success('📬 Consigne Expert envoyée au client !');
      setEditingConsigne(false);
      fetchMissionDetails(); // Rafraîchir les données
    } catch (err) {
      toast.error('Erreur lors de l\'envoi de la consigne');
    } finally {
      setSavingConsigne(false);
    }
  };
  
  // Télécharger un document
  const handleDownloadDocument = async (docId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/documents/${docId}/view`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Erreur</h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <Link to="/admin-dashboard">
            <Button className="bg-cyan-400 text-black hover:bg-cyan-300">
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour au Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const { client, addresses, technical, contracts, documents, mandat } = mission || {};

  // V1.2 : détection du type de mission pour adapter la vue admin
  const missionPackType = mission?.mission?.pack_type || mission?.details?.pack_type || '';
  const isSuccession = /heritage|succession|serenite/i.test(missionPackType);
  const deceased = mission?.mission?.deceased
    || mission?.details?.deceased
    || mission?.mission?.mission_data?.client
    || {};
  const missionCategories = mission?.mission?.mission_data?.categories
    || mission?.details?.mission_data?.categories
    || {};
  
  return (
    <div className="min-h-screen bg-void p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header avec indicateur temps réel */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5 mr-2" /> Retour
              </Button>
            </Link>
            <img src={LOGO_URL} alt="L'Effaceur" className="h-10" />
          </div>
          <div className="flex items-center gap-4">
            {/* V5.48 : Indicateur temps réel */}
            <div className="flex items-center gap-2">
              {hasNewData && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-3 h-3 bg-green-400 rounded-full animate-pulse"
                  title="Nouvelles données disponibles"
                />
              )}
              <span className="text-gray-500 text-xs font-mono">
                {lastUpdate ? `Màj: ${lastUpdate.toLocaleTimeString('fr-FR')}` : '-'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setHasNewData(false);
                  fetchMissionDetails();
                }}
                className="text-gray-400 hover:text-cyan-400"
                title="Rafraîchir"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="text-right">
              <p className="text-cyan-400 font-mono text-sm">Référence Dossier</p>
              {/* V5.52 : Utiliser display_id (EFF-XXXX) au lieu de mission_id technique */}
              <p className="text-white font-bold text-lg" data-testid="mission-display-id">
                {mission?.display_id || mission?.mission_id || missionId}
              </p>
            </div>
          </div>
        </div>
        
        {/* Statut et Pack */}
        <div className="glass-card p-6 border border-cyan-400/30 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-orbitron text-2xl font-bold text-white">
                {mission?.mission?.pack_type === 'mission_demenagement' ? 'Mission Déménagement' : 
                 mission?.mission?.pack_type === 'mission_heritage' ? 'Mission Héritage' : 
                 mission?.mission?.pack_type === 'demenagement' ? 'Mission Déménagement' :
                 'Mission Audit'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Créée le {mission?.mission?.created_at ? new Date(mission.mission.created_at).toLocaleDateString('fr-FR') : '-'}
              </p>
            </div>
            
            {/* V5.51 : Outils Expert - Copie Rapide & Téléchargement */}
            <div className="flex items-center gap-3">
              {/* Copie Rapide */}
              <Button
                onClick={handleCopyDossier}
                disabled={copyingDossier}
                className="bg-purple-500 hover:bg-purple-400 text-white font-bold"
                title="Copier le dossier complet pour l'agent IA"
                data-testid="copy-dossier-btn"
              >
                {copyingDossier ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ClipboardList className="w-4 h-4 mr-2" />
                )}
                Copie Rapide
              </Button>
              
              {/* Télécharger Mandat */}
              {mandat?.exists && (
                <Button
                  onClick={handleDownloadMandat}
                  disabled={downloadingMandat}
                  className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold"
                  title="Télécharger le mandat PDF signé"
                  data-testid="download-mandat-admin-btn"
                >
                  {downloadingMandat ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Télécharger Mandat
                </Button>
              )}
              
              {/* V5.54 : Export CSV de CE dossier uniquement (isolé) */}
              <Button
                onClick={handleExportSingleCSV}
                disabled={exportingCSV}
                className="bg-green-500 hover:bg-green-400 text-white font-bold"
                title="Exporter ce dossier en CSV"
                data-testid="export-single-csv-btn"
              >
                {exportingCSV ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Exporter ce dossier (CSV)
              </Button>

              {/* DDD.9 : Téléchargement complet en ZIP — CSV + tous les mandats + docs client */}
              <Button
                onClick={handleDownloadBundle}
                disabled={downloadingMandat}
                className="bg-magenta-500 hover:bg-magenta-400 text-white font-bold"
                title="Télécharger TOUT le dossier en ZIP (CSV + mandats par fournisseur + docs client)"
                data-testid="download-bundle-btn"
              >
                {downloadingMandat ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Tout télécharger (ZIP)
              </Button>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-mono ${
              mission?.mission?.status === 'VALIDATED' ? 'bg-green-400/20 text-green-400 border border-green-400/50' :
              mission?.mission?.status === 'en_cours_traitement' ? 'bg-yellow-400/20 text-yellow-400 border border-yellow-400/50' :
              'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50'
            }`}>
              {mission?.mission?.status_label || mission?.mission?.status || 'Nouveau'}
            </div>
          </div>
        </div>
        
        {/* V5.71 : BORDEREAU DE STATUT INLINE — Pilotage depuis la fiche détail */}
        <StatusBordereau
          currentStatus={mission?.mission?.status}
          onStatusChange={handleStatusChange}
          updating={updatingStatus}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* V1.2 : Bloc HÉRITAGE - Informations sur le défunt + catégories à résilier */}
          {isSuccession && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 border-2 border-magenta-400/50 bg-magenta-400/5 lg:col-span-2"
            >
              <h2 className="font-orbitron text-xl font-bold text-magenta-400 mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6" /> Informations du défunt
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                  <p className="text-gray-400 text-xs font-mono mb-2">// NOM_DU_DÉFUNT</p>
                  <p className="text-white font-medium">{deceased?.nom_defunt || deceased?.name || deceased?.nom_prenom || 'Non communiqué'}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                  <p className="text-gray-400 text-xs font-mono mb-2">// DATE_DU_DÉCÈS</p>
                  <p className="text-white font-medium">{deceased?.date_deces || deceased?.death_date || 'Non communiquée'}</p>
                </div>
                <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                  <p className="text-gray-400 text-xs font-mono mb-2">// LIEN_DE_PARENTÉ</p>
                  <p className="text-white font-medium">{deceased?.lien_parente || deceased?.relation || 'Non communiqué'}</p>
                </div>
              </div>
              {/* Catégories résiliées */}
              <div className="mt-6">
                <h3 className="text-magenta-400 font-mono text-sm mb-3">// ORGANISMES_À_RÉSILIER</h3>
                {Object.keys(missionCategories).length === 0 ? (
                  <p className="text-gray-500 text-sm italic">Aucune catégorie sélectionnée (en attente saisie client)</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(missionCategories).filter(([, v]) => v?.status === 'desactive').map(([catId, v]) => (
                      <div key={catId} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-white font-medium text-sm">{v?.provider || catId}</p>
                        <p className="text-red-400 text-xs">À résilier</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* V5.48 : BLOC CENTRAL - Adresses (uniquement pour Déménagement) */}
          {!isSuccession && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border-2 border-cyan-400/50 bg-cyan-400/5 lg:col-span-2"
          >
            <h2 className="font-orbitron text-xl font-bold text-cyan-400 mb-6 flex items-center gap-2">
              <MapPin className="w-6 h-6" /> Adresses du Déménagement
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* V5.58 : Ancienne Adresse - Alerte si vide */}
              <div className={`p-4 rounded-lg ${addresses?.current?.address ? 'bg-red-400/10 border border-red-400/30' : 'bg-red-900/30 border-2 border-red-500/50'}`}>
                <p className={`text-xs font-mono mb-3 flex items-center gap-2 ${addresses?.current?.address ? 'text-red-400' : 'text-red-500'}`}>
                  <span className={`w-2 h-2 rounded-full ${addresses?.current?.address ? 'bg-red-400' : 'bg-red-500 animate-pulse'}`}></span>
                  // ANCIENNE_ADRESSE (DÉPART)
                </p>
                {addresses?.current?.address ? (
                  <>
                    <p className="text-lg font-medium text-white">{addresses.current.address}</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {addresses.current.postal_code || '?????'} {addresses.current.city || 'Ville non communiquée'}
                    </p>
                    <div className="mt-3">
                      <CopyButton 
                        text={`${addresses.current.address}, ${addresses.current.postal_code} ${addresses.current.city}`} 
                        label="Ancienne adresse complète" 
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">⚠️ Erreur de synchronisation tunnel</span>
                  </div>
                )}
              </div>
              
              {/* Date de déménagement - V5.58 avec alerte */}
              <div className={`p-4 rounded-lg flex flex-col items-center justify-center ${addresses?.moving_date ? 'bg-cyan-400/10 border border-cyan-400/30' : 'bg-orange-900/20 border-2 border-orange-500/50'}`}>
                <Calendar className={`w-8 h-8 mb-2 ${addresses?.moving_date ? 'text-cyan-400' : 'text-orange-400'}`} />
                <p className="text-gray-400 text-xs font-mono mb-1">// DATE_DEMENAGEMENT</p>
                {addresses?.moving_date ? (
                  <p className="text-2xl font-bold font-mono text-cyan-400">
                    {new Date(addresses.moving_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </p>
                ) : (
                  <p className="text-orange-400 text-sm font-medium flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Date non communiquée
                  </p>
                )}
              </div>
              
              {/* V5.58 : Nouvelle Adresse - Alerte si vide */}
              <div className={`p-4 rounded-lg ${addresses?.new?.address ? 'bg-green-400/10 border border-green-400/30' : 'bg-orange-900/20 border-2 border-orange-500/50'}`}>
                <p className={`text-xs font-mono mb-3 flex items-center gap-2 ${addresses?.new?.address ? 'text-green-400' : 'text-orange-400'}`}>
                  <span className={`w-2 h-2 rounded-full ${addresses?.new?.address ? 'bg-green-400' : 'bg-orange-400 animate-pulse'}`}></span>
                  // NOUVELLE_ADRESSE (ARRIVÉE)
                </p>
                {addresses?.new?.address ? (
                  <>
                    <p className="text-lg font-medium text-white">{addresses.new.address}</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {addresses.new.postal_code || '?????'} {addresses.new.city || 'Ville non communiquée'}
                    </p>
                    <div className="mt-3">
                      <CopyButton 
                        text={`${addresses.new.address}, ${addresses.new.postal_code} ${addresses.new.city}`} 
                        label="Nouvelle adresse complète" 
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-orange-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">⚠️ Non communiquée</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          )}

          {/* Section 1 : Identité du client */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6 border border-gray-800"
          >
            <h2 className="font-orbitron text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" /> Identité du Mandant
            </h2>
            <div className="space-y-3">
              <DataField label="Prénom" value={client?.first_name || 'Non communiqué'} icon={User} />
              <DataField label="Nom" value={client?.last_name || 'Non communiqué'} icon={User} />
              <DataField label="Email" value={client?.email || 'Non communiqué'} icon={Mail} />
              <DataField label="Téléphone" value={client?.phone || 'Non communiqué'} icon={Phone} />
            </div>
          </motion.div>
          
          {/* Section 3 : Données techniques (PDL, PCE, Notes) - V5.48 VISIBILITÉ FORCÉE */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6 border border-cyan-400/30"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-orbitron text-lg font-bold text-cyan-400 flex items-center gap-2">
                <Zap className="w-5 h-5" /> Données Techniques
              </h2>
              {/* V5.48 : Bouton édition pour l'admin */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingTechnical(!editingTechnical)}
                className={editingTechnical ? 'text-cyan-400' : 'text-gray-400 hover:text-cyan-400'}
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {editingTechnical ? 'Annuler' : 'Modifier'}
              </Button>
            </div>
            
            {/* V5.48 : Mode édition ou lecture */}
            {editingTechnical ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-yellow-400 text-xs font-mono flex items-center gap-2">
                    <Zap className="w-3 h-3" /> PDL (Point de Livraison Électricité)
                  </label>
                  <Input
                    value={technicalData.pdl}
                    onChange={(e) => setTechnicalData(prev => ({ ...prev, pdl: e.target.value }))}
                    placeholder="Ex: 12345678901234"
                    className="bg-gray-800/50 border-gray-700"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-orange-400 text-xs font-mono flex items-center gap-2">
                    <Zap className="w-3 h-3" /> PCE (Point de Comptage Gaz)
                  </label>
                  <Input
                    value={technicalData.pce}
                    onChange={(e) => setTechnicalData(prev => ({ ...prev, pce: e.target.value }))}
                    placeholder="Ex: GI123456"
                    className="bg-gray-800/50 border-gray-700"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-cyan-400 text-xs font-mono flex items-center gap-2">
                    <Clipboard className="w-3 h-3" /> Notes complémentaires
                  </label>
                  <textarea
                    value={technicalData.notes}
                    onChange={(e) => setTechnicalData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Informations complémentaires..."
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-white placeholder:text-gray-500 min-h-[100px]"
                  />
                </div>
                <Button
                  onClick={handleSaveTechnical}
                  disabled={savingTechnical}
                  className="bg-cyan-400 text-black hover:bg-cyan-300 w-full"
                >
                  {savingTechnical ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer les modifications
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* V5.48 : Affichage avec valeur par défaut si vide */}
                <div className="flex items-center justify-between p-3 bg-yellow-400/10 rounded-lg border border-yellow-400/30">
                  <div className="flex items-center gap-3">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <div>
                      <p className="text-gray-400 text-xs">PDL (Point de Livraison Électricité)</p>
                      <p className={`font-mono text-sm ${technical?.pdl ? 'text-white' : 'text-orange-400 italic'}`}>
                        {technical?.pdl || 'En attente de saisie client'}
                      </p>
                    </div>
                  </div>
                  {technical?.pdl && <CopyButton text={technical.pdl} label="PDL" />}
                </div>
                
                <div className="flex items-center justify-between p-3 bg-orange-400/10 rounded-lg border border-orange-400/30">
                  <div className="flex items-center gap-3">
                    <Zap className="w-4 h-4 text-orange-400" />
                    <div>
                      <p className="text-gray-400 text-xs">PCE (Point de Comptage Gaz)</p>
                      <p className={`font-mono text-sm ${technical?.pce ? 'text-white' : 'text-orange-400 italic'}`}>
                        {technical?.pce || 'En attente de saisie client'}
                      </p>
                    </div>
                  </div>
                  {technical?.pce && <CopyButton text={technical.pce} label="PCE" />}
                </div>
                
                {/* Notes */}
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-400 text-xs flex items-center gap-2">
                      <Clipboard className="w-4 h-4" /> Notes complémentaires
                    </p>
                    {technical?.notes && <CopyButton text={technical.notes} label="Notes" />}
                  </div>
                  <p className={`text-sm whitespace-pre-wrap ${technical?.notes ? 'text-white' : 'text-orange-400 italic'}`}>
                    {technical?.notes || 'En attente de saisie client'}
                  </p>
                </div>
                
                {/* V5.48 : Indicateur de mise à jour technique */}
                {mission?.technical_updated_at && (
                  <p className="text-gray-500 text-xs flex items-center gap-1 mt-2">
                    <Clock className="w-3 h-3" />
                    Dernière màj technique: {new Date(mission.technical_updated_at).toLocaleString('fr-FR')}
                  </p>
                )}
              </div>
            )}
          </motion.div>
          
          {/* V5.63 : Section Consigne Expert + V5.70 Messages thread + Avis */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-card p-6 border-2 border-purple-400/50 bg-purple-400/5"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-orbitron text-lg font-bold text-purple-400 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" /> Dialogue Expert ↔ Client
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingConsigne(!editingConsigne)}
                className={editingConsigne ? 'text-purple-400' : 'text-gray-400 hover:text-purple-400'}
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {editingConsigne ? 'Annuler' : 'Écrire'}
              </Button>
            </div>
            
            {/* Consigne admin */}
            {editingConsigne ? (
              <div className="space-y-4 mb-4">
                <textarea
                  value={consigneExpert}
                  onChange={(e) => setConsigneExpert(e.target.value)}
                  placeholder="Message visible par le client..."
                  className="w-full bg-gray-800/50 border border-purple-400/30 rounded-lg p-4 text-white placeholder:text-gray-500 min-h-[100px] focus:border-purple-400 focus:outline-none"
                />
                <Button onClick={handleSaveConsigne} disabled={savingConsigne} className="bg-purple-500 hover:bg-purple-400 text-white w-full">
                  {savingConsigne ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer au client
                </Button>
              </div>
            ) : mission?.consigne_expert?.text ? (
              <div className="p-4 bg-purple-400/10 rounded-lg border border-purple-400/30 mb-4">
                <p className="text-xs font-mono text-purple-400 mb-1">VOUS (Expert)</p>
                <p className="text-white whitespace-pre-wrap text-sm">{mission.consigne_expert.text}</p>
                {mission.consigne_expert.updated_at && (
                  <p className="text-gray-500 text-xs mt-2">{new Date(mission.consigne_expert.updated_at).toLocaleString('fr-FR')}</p>
                )}
              </div>
            ) : null}
            
            {/* V5.70 : Thread de messages client */}
            {mission?.messages_thread && mission.messages_thread.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-xs font-mono text-gray-500 mb-2">RÉPONSES CLIENT :</p>
                {mission.messages_thread.map((msg, idx) => (
                  <div key={msg.id || idx} className="p-3 bg-cyan-400/10 border border-cyan-400/20 rounded-lg ml-4">
                    <p className="text-xs font-mono text-cyan-400 mb-1">{msg.author_name || 'Client'} — {new Date(msg.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    <p className="text-white text-sm">{msg.text}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* V5.70 : Avis client final */}
            {mission?.client_avis && (
              <div className="p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-lg">
                <p className="text-xs font-mono text-yellow-400 mb-2">AVIS FINAL CLIENT</p>
                <p className="text-white text-sm italic">"{mission.client_avis.text}"</p>
                <p className="text-gray-500 text-xs mt-2">{mission.client_avis.author_name} — {mission.client_avis.submitted_at ? new Date(mission.client_avis.submitted_at).toLocaleDateString('fr-FR') : ''}</p>
              </div>
            )}
            
            {!mission?.consigne_expert?.text && !mission?.messages_thread?.length && !mission?.client_avis && (
              <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <p className="text-gray-400 italic text-sm">Aucun message — cliquez "Écrire" pour envoyer une consigne au client</p>
              </div>
            )}
          </motion.div>
          
          {/* Section 4 : Contrats / Fournisseurs */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6 border border-gray-800"
          >
            <h2 className="font-orbitron text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Contrats Sélectionnés
            </h2>
            {contracts && contracts.length > 0 ? (
              <div className="space-y-3">
                {contracts.map((contract, idx) => {
                  const config = CATEGORY_CONFIG[contract.category] || {};
                  const Icon = config.icon || Building2;
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${config.color || 'text-gray-400'}`} />
                        <div>
                          <p className="text-white font-medium">{contract.provider || config.label || contract.category}</p>
                          <p className="text-gray-400 text-xs">{config.label}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-mono ${
                        contract.action === 'Transfert' 
                          ? 'bg-green-400/20 text-green-400' 
                          : 'bg-red-400/20 text-red-400'
                      }`}>
                        {contract.action}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">Aucun contrat sélectionné</p>
            )}
          </motion.div>
          
          {/* Section 5 : Documents uploadés */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 border border-gray-800 lg:col-span-2"
          >
            <h2 className="font-orbitron text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Documents Uploadés
            </h2>
            {documents && documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc, idx) => (
                  <div key={idx} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-cyan-400/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-cyan-400" />
                        <div>
                          <p className="text-white text-sm font-medium truncate max-w-[150px]">
                            {doc.filename || doc.doc_type || 'Document'}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownloadDocument(doc.document_id, doc.filename)}
                          className="p-2 rounded hover:bg-cyan-400/20 transition-colors"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4 text-cyan-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">Aucun document uploadé</p>
            )}
          </motion.div>
          
          {/* Section 6 : Mandat */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6 border border-gray-800 lg:col-span-2"
          >
            <h2 className="font-orbitron text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" /> Mandat de Délégation
            </h2>
            {mandat?.exists ? (
              <div className="space-y-4">
                {/* Mandat global signé */}
                <div className="flex items-center justify-between p-4 bg-green-400/10 border border-green-400/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <Check className="w-8 h-8 text-green-400" />
                    <div>
                      <p className="text-green-400 font-bold">Mandat Global Signé</p>
                      <p className="text-gray-400 text-sm">
                        ID: {mandat.mandat_id} | Signé le {mandat.signed_at ? new Date(mandat.signed_at).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleDownloadMandat}
                    disabled={downloadingMandat}
                    className="bg-cyan-400 text-black hover:bg-cyan-300"
                  >
                    {downloadingMandat ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Télécharger PDF</>}
                  </Button>
                </div>

                {/* V1.2 : Mandats unitaires par organisme (pour l'agent) */}
                {mandat?.unit_categories?.length > 0 && (
                  <div className="p-4 bg-magenta-400/5 border border-magenta-400/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-magenta-400" />
                        <h3 className="text-magenta-400 font-bold">Mandats unitaires par organisme</h3>
                        <span className="px-2 py-0.5 bg-magenta-400/20 text-magenta-300 text-xs rounded font-mono">
                          {mandat.unit_categories.length} courrier{mandat.unit_categories.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs italic">Prêts à envoyer par LRAR / email</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {mandat.unit_categories.map((cat) => {
                        const cid = typeof cat === 'string' ? cat : cat?.category_id;
                        const clabel = typeof cat === 'string' ? cat : (cat?.provider || cat?.title || cat?.category_id);
                        const caction = typeof cat === 'object' ? cat?.action : null;
                        return (
                          <div key={cid} className="flex items-center justify-between p-3 bg-gray-900/50 border border-gray-700 rounded-lg hover:border-magenta-400/50 transition-all">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-md bg-magenta-400/20 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-4 h-4 text-magenta-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate">{clabel || cid}</p>
                                {caction && (
                                  <p className={`text-xs ${caction === 'liquidate' || caction === 'desactive' ? 'text-red-400' : 'text-emerald-400'}`}>
                                    {caction === 'liquidate' || caction === 'desactive' ? '✗ Résiliation' : '✓ Transfert'}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleDownloadUnitMandat(cid, clabel || cid)}
                              className="bg-magenta-400/20 text-magenta-300 hover:bg-magenta-400/30 border border-magenta-400/40"
                            >
                              <Download className="w-3 h-3 mr-1" /> PDF
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-lg">
                <Clock className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-yellow-400 font-bold">Mandat en attente</p>
                  <p className="text-gray-400 text-sm">Le client n'a pas encore signé le mandat</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
